export const getPlayedColors = (player) => {
    if (!player.matches)
        return [];
    return player.matches
        .filter((m) => !m.bye)
        .map((m) => (m.seating === 1 ? "w" : "b"));
};
export const getPlayedColorPref = (player) => {
    const colors = getPlayedColors(player);
    const colorPref = colors.reduce((value, color) => {
        return color === "w" ? value + 1 : value - 1;
    }, 0);
    return colorPref;
};
export const getColorPref = (round, player) => {
    if (!round.standings?.length)
        return "+0";
    const standing = round.standings.find((s) => {
        return s.player.id === player.id || s.player.id === player.username;
    });
    if (!standing?.player) {
        return "+0";
    }
    const standingPlayer = standing.player;
    const colors = getPlayedColors(standingPlayer);
    const colorPref = getPlayedColorPref(standingPlayer);
    return `[${colors.join("")}]${colorPref >= 0 ? "+" : ""}${colorPref}`;
};
export const isPlayerInRound = (player, roundNumber) => {
    if (!roundNumber)
        roundNumber = 1;
    const mask = 1 << (roundNumber - 1);
    return !!(player.roundsParticipated & mask);
};
/**
 * Calculate the appropriate rounds participated bitmask for a player
 * This is migrated from TournamentPlayerService.calculateRoundsParticipated
 *
 * Business Logic:
 * - Players cannot join rounds that are already in progress
 * - If currentRound >= totalRounds (final round or finished), no default participation
 * - Explicit roundNumber overrides the "too late" restriction
 *
 * @param currentRound The tournament's current round (0 if not started)
 * @param totalRounds Total number of rounds in the tournament
 * @param existingRoundsParticipated Existing rounds participated bitmask (for existing players)
 * @param roundNumber The specific round number to modify (optional, defaults to next available round or none if too late)
 * @param participate Whether this player should participate
 * @returns The appropriate rounds participated bitmask
 */
export const calculateRoundsParticipated = (currentRound, totalRounds, existingRoundsParticipated, roundNumber, participate = true) => {
    const toRoundNumber = !roundNumber || roundNumber >= currentRound ? totalRounds : roundNumber;
    if (!roundNumber) {
        // If no specific round is provided, check if it's too late to join
        if (currentRound >= totalRounds) {
            // Tournament is on final round or finished - too late to join by default
            return existingRoundsParticipated; // No change to participation
        }
        roundNumber = Math.min(currentRound + 1, totalRounds);
    }
    const bitMask = (1 << toRoundNumber) - (1 << (roundNumber - 1));
    return participate
        ? existingRoundsParticipated | bitMask
        : existingRoundsParticipated & ~bitMask;
};
/**
 * Calculate initial rounds participated bitmask for new players being added to a tournament
 * @param currentRound The tournament's current round (0 if not started)
 * @param totalRounds Total number of rounds in the tournament
 * @param shouldParticipate Whether this player should participate in the tournament
 * @returns The appropriate initial rounds participated bitmask
 */
export const calculateInitialRoundsParticipated = (currentRound, totalRounds, shouldParticipate) => {
    if (!shouldParticipate) {
        return 0; // Non-participating players have no rounds
    }
    if (currentRound === 0) {
        // Tournament not started - player will participate in all rounds
        return (1 << totalRounds) - 1; // All bits set for rounds 1 to totalRounds
    }
    else if (currentRound > totalRounds) {
        // Tournament finished - new players can't participate
        return 0;
    }
    else {
        // Tournament in progress - player participates from current round onwards
        const fromRound = currentRound;
        const toRound = totalRounds;
        return (1 << toRound) - (1 << (fromRound - 1));
    }
};
